import { useEffect, useState, useRef, useCallback, startTransition } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiBell, FiCheckCircle, FiClock, FiX, FiLock, FiUnlock, FiMove } from 'react-icons/fi';
import LoadingIcon from './LoadingIcon';
import { useClient } from '../contexts/ClientContext';
import { usePageVisibility } from '../hooks/usePageVisibility';
import { toZonedTime, fromZonedTime, formatInTimeZone } from 'date-fns-tz';
import { getReminder, saveReminderAsTask, updateReminder, getClientReminders, getUserReminders, completeReminder } from '../api/remindersApi';
import { getAdminTaskById, getManagerTaskById, updateAdminTask, updateManagerTask } from '../api/tasksApi';
import { apiClient } from '../api/apiClient';
import { getNotifications } from '../api/notificationsApi';
import { useSocketSubscription, useSocketConnection } from '../lib/socketManager';
import { getSocketUrl } from '../utils/socketUrl';
import { getCompanySlugFromLocation, resolveActiveCompanySlug, getRuntimeEnvFromHostname } from '../utils/companySlug';
import { logRateLimited, errorRateLimited, warnRateLimited } from '../utils/logRateLimit';
import { rateLog, rateOnce } from '../utils/rateLog';
import { shouldSkipNetwork, isNetworkError, markNetworkDown, markNetworkUp } from '../utils/networkGate';
import { getPollingInterval } from '../config/polling';

// Get user's timezone
const getUserTimeZoneLocal = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Jerusalem';
  } catch {
    return 'Asia/Jerusalem';
  }
};

// Convert datetime-local value to timezone-aware format (same as ClientProfile)
const localDateTimeToTimezoneAware = (localDateTime: string): { isoLocal: string; targetMs: number; timeZone: string } | null => {
  if (!localDateTime) return null;
  const [datePart, timePart] = localDateTime.split('T');
  if (!datePart || !timePart) return null;
  
  const [year, month, day] = datePart.split('-').map(Number);
  const [hours, minutes] = timePart.split(':').map(Number);
  
  const timeZone = getUserTimeZoneLocal();
  
  // Create a date object representing the local time components
  // We'll treat this as if it's in the target timezone
  const localDate = new Date(year, month - 1, day, hours, minutes, 0, 0);
  
  // Use fromZonedTime: treats the date as if it's in the target timezone and converts to UTC
  const utcDate = fromZonedTime(localDate, timeZone);
  const targetMs = utcDate.getTime();
  
  // Format the UTC date in the target timezone to get ISO with offset
  const isoLocal = formatInTimeZone(utcDate, timeZone, "yyyy-MM-dd'T'HH:mm:ssXXX");
  
  return { isoLocal, targetMs, timeZone };
};

const baseUrl = getSocketUrl();

interface Task {
  _id: string;
  title: string;
  description?: string;
  deadline?: string | null;
  isCalendar?: boolean;
  triggerAt?: string | null;
  reminderSentAt?: string | null;
  reminderTargetMs?: number;
  reminderIsoLocal?: string;
  reminderTimeZone?: string;
  reminderDeliveredAtMs?: number;
  reminderStatus?: 'pending' | 'delivered' | 'completed';
  status?: string;
  managerId?: string | { _id: string } | string;
  createdBy?: string | { _id: string };
  remindBeforeMinutes?: number;
  // Bell state management (persisted, not time-based)
  bellActive?: boolean; // true = GREEN bell, false = OFF/neutral
  completedAtMs?: number; // When user pressed "הבנתי"
  __fromReminder?: boolean;
}

interface TaskLiteUser {
  _id: string;
  fullName?: string;
  email?: string;
}

interface ReminderPopupData {
  taskId: string;
  reminderId?: string; // CRITICAL: Store reminderId for dedupe (not taskId)
  title: string;
  description?: string;
  deadline?: string | null;
  targetMs?: number;
  timeZone?: string;
  isoLocal?: string;
}

/** Get first path segment as tenant, ignoring reserved app prefixes */
function tenantFromPathname(pathname: string): string {
  const seg = pathname.split('/').filter(Boolean)[0] || '';
  if (['admin', 'manager', 'agent', 'app'].includes(seg)) return '';
  return seg;
}

// Get user's timezone
const getUserTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Jerusalem';
  } catch {
    return 'Asia/Jerusalem';
  }
};

// BroadcastChannel for multi-tab coordination
const REMINDER_CHANNEL = 'reminder-popup-channel';

export default function GlobalReminderPopup() {
  const location = useLocation();
  const { client: clientData } = useClient();
  const isVisible = usePageVisibility(); // Pause polling when tab is hidden
  const [reminderPopup, setReminderPopup] = useState<ReminderPopupData | null>(null);
  const [completingReminder, setCompletingReminder] = useState(false);
  const [reschedulingReminder, setReschedulingReminder] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const [remindLaterDateTime, setRemindLaterDateTime] = useState('');
  const isSocketConnected = useSocketConnection();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const heartbeatRef = useRef<NodeJS.Timeout | null>(null);
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);
  
  // Track which tab showed which reminder to prevent same-tab auto-close
  const tabReminderShownRef = useRef<Set<string>>(new Set());
  
  // Track reminders that failed to complete (to prevent popup from reappearing immediately)
  const failedRemindersRef = useRef<Map<string, number>>(new Map());
  
  // Initialize BroadcastChannel for cross-tab dedupe
  useEffect(() => {
    if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
      broadcastChannelRef.current = new BroadcastChannel(REMINDER_CHANNEL);
      
      // Listen for reminder:due events from other tabs
      broadcastChannelRef.current.onmessage = (event) => {
        // PERFORMANCE FIX: Use setTimeout(0) instead of Promise.resolve() to truly defer execution
        // This moves execution to the next event loop tick, preventing [Violation] warnings
        setTimeout(() => {
          const { type, reminderId, targetMs, tabId } = event.data;
          
          // CRITICAL FIX: Ignore messages from the same tab (prevent self-closing)
          const currentTabId = sessionStorage.getItem('tabId') || 'default';
          if (tabId === currentTabId) {
            return; // This message came from this tab, ignore it
          }
          
          if (type === 'reminder:due') {
            // Another tab is showing this reminder - mark it as shown to prevent duplicate
            const reminderKey = `reminder-${reminderId}-${targetMs}`;
            shownRemindersRef.current.add(reminderKey);
            // Defer localStorage operations to avoid blocking
            setTimeout(() => {
              localStorage.setItem(`reminder-shown-${reminderId}`, String(targetMs));
            }, 0);
          } else if (type === 'reminder:completed') {
            // Another tab completed this reminder - mark as completed
            // Defer localStorage operations to avoid blocking
            setTimeout(() => {
              const completedKey = `reminder-completed-${reminderId}`;
              localStorage.setItem(completedKey, String(Date.now()));
            }, 0);
            // Use startTransition for state updates to make them non-blocking
            startTransition(() => {
              setReminderPopup((prev) => {
                if (prev && String(prev.taskId) === String(reminderId)) {
                  updateReminderPopup(null);
                  return null;
                }
                return prev;
              });
            });
          }
        }, 0);
      };
    }
    
    // Generate unique tab ID for this session
    if (typeof window !== 'undefined' && !sessionStorage.getItem('tabId')) {
      sessionStorage.setItem('tabId', `tab-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
    }
    
    return () => {
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.close();
        broadcastChannelRef.current = null;
      }
    };
  }, []);
  const dragStartRef = useRef<{ x: number; y: number } | null>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);

  const isAdmin = clientData?.role === 'admin';
  
  // Use centralized helper to resolve company slug - handles all cases including root path
  const slugFromUrl = getCompanySlugFromLocation();
  const storedCompany = typeof window !== 'undefined' ? localStorage.getItem('currentCompany') || localStorage.getItem('wjs_active_company_v2') || '' : '';
  const clientCompany = (clientData as any)?.company?.slug || (clientData as any)?.company || (clientData as any)?.companySlug || '';
  const env = getRuntimeEnvFromHostname();
  const resolvedCompany = resolveActiveCompanySlug({
    authUserSlug: clientCompany || storedCompany || undefined,
    env,
  });
  const companyCandidate = slugFromUrl || resolvedCompany || storedCompany || clientCompany || '';
  const company = companyCandidate && companyCandidate !== 'superadmin' ? companyCandidate : '';
  const userTimeZone = getUserTimeZoneLocal();
  const isLocalApiButRemoteClient = (() => {
    if (typeof window === 'undefined') return false;
    try {
      const apiHost = new URL(baseUrl).hostname;
      const pageHost = window.location.hostname;
      const isApiLocal = ['localhost', '127.0.0.1'].includes(apiHost);
      const isPageLocal = ['localhost', '127.0.0.1', '0.0.0.0'].includes(pageHost);
      return isApiLocal && !isPageLocal;
    } catch {
      return false;
    }
  })();

  const remindersPollStateRef = useRef<{ timer: NodeJS.Timeout | null; inFlight: boolean; failures: number }>({ timer: null, inFlight: false, failures: 0 });
  const reminderApiStateRef = useRef<{ failCount: number; skipUntil: number }>({ failCount: 0, skipUntil: 0 });
  const reminderLegacyStateRef = useRef<{ failCount: number; skipUntil: number; triedOnce: boolean }>({ failCount: 0, skipUntil: 0, triedOnce: false });
  const legacyTimeoutLoggedRef = useRef(false);
  const wasVisibleRef = useRef<boolean | null>(null); // Track previous visibility state
  const shownRemindersRef = useRef<Set<string>>(new Set()); // Track which reminders have been shown to prevent duplicates
  const previousPopupRef = useRef<ReminderPopupData | null>(null); // Track previous popup state for shallow compare
  const reminderPopupRef = useRef<ReminderPopupData | null>(null); // Track current popup state in ref for async checks
  
  // CRITICAL FIX: Helper to check if reminder was shown (checks both memory and localStorage)
  const isReminderShown = useCallback((reminderId: string, targetMs: number): boolean => {
    const reminderKey = `reminder-${reminderId}-${targetMs}`;
    
    // Check memory first (fast)
    if (shownRemindersRef.current.has(reminderKey)) {
      return true;
    }
    
    // CRITICAL: Also check localStorage (persistent across page reloads)
    const shownInStorage = localStorage.getItem(`reminder-shown-${reminderId}`);
    if (shownInStorage) {
      const shownTargetMs = parseInt(shownInStorage, 10);
      // If shown within 1 hour of target time, consider it shown (handles slight time differences)
      if (Math.abs(shownTargetMs - targetMs) < 60 * 60 * 1000) {
        // Add to memory cache for faster future checks
        shownRemindersRef.current.add(reminderKey);
        return true;
      }
    }
    
    return false;
  }, []);
  
  // CRITICAL FIX: Helper to mark reminder as shown (both memory and localStorage)
  const markReminderAsShown = useCallback((reminderId: string, targetMs: number): void => {
    const reminderKey = `reminder-${reminderId}-${targetMs}`;
    shownRemindersRef.current.add(reminderKey);
    localStorage.setItem(`reminder-shown-${reminderId}`, String(targetMs));
  }, []);

  // Helper: Update reminder popup state - CRITICAL FIX: Always update to ensure popup shows
  const updateReminderPopup = useCallback((newPopup: ReminderPopupData | null) => {
    if (newPopup === null) {
      if (previousPopupRef.current !== null) {
        previousPopupRef.current = null;
        reminderPopupRef.current = null;
        setReminderPopup(null);
      }
      return;
    }
    
    // CRITICAL FIX: Always update state to ensure popup shows immediately
    // Don't use shallow compare for immediate reminders - it can prevent popup from showing
    // Update refs first for immediate access, then state
    previousPopupRef.current = newPopup;
    reminderPopupRef.current = newPopup;
    
    // CRITICAL FIX: Update state directly (not functional) for immediate rendering
    // This ensures the popup appears instantly without React batching delays
    setReminderPopup(newPopup);
    
  }, []);

  const ensureReminderTask = useCallback(async (reminderId?: string | null, isTaskBased: boolean = false) => {
    // CRITICAL FIX: Skip for task-based reminders (they're already tasks, not reminder documents)
    if (isTaskBased || !reminderId || !company) return null;
    
    const storageKey = `reminder-task-saved-${reminderId}`;

    const fetchReminderTaskId = async () => {
      try {
        const reminderRes = await getReminder(company, reminderId);
        const reminder = (reminderRes as any)?.reminder;
        if (reminder?.taskId) {
          if (typeof window !== 'undefined') {
            localStorage.setItem(storageKey, reminder.taskId);
          }
          return reminder.taskId as string;
        }
      } catch (err: any) {
        // CRITICAL FIX: Only log if it's not a 404 (reminder doesn't exist)
        // 404 means this is a task-based reminder, which is expected
        if (err?.response?.status !== 404) {
          rateLog('reminder-fetch-taskid-error', 'error', 15000, '❌ [GlobalReminderPopup] Failed to fetch reminder taskId:', err);
        }
      }
      return null;
    };

    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        if (stored !== '1') {
          return { task: { _id: stored } };
        }
        const fallbackId = await fetchReminderTaskId();
        if (fallbackId) {
          return { task: { _id: fallbackId } };
        }
      }
    }

    try {
      const response = await saveReminderAsTask(company, reminderId);
      const createdTaskId = (response as any)?.task?._id;
      if (typeof window !== 'undefined') {
        localStorage.setItem(storageKey, createdTaskId || '1');
      }
      return response;
    } catch (err: any) {
      // CRITICAL FIX: Handle different error types gracefully
      // 404 means reminder doesn't exist (task-based reminder)
      // 500 with "kindCode is required" means backend needs kindCode but we can't provide it for reminders
      // In both cases, try to fetch existing taskId as fallback
      const is404 = err?.response?.status === 404;
      const isKindCodeError = err?.response?.data?.error?.includes('kindCode') || 
                              err?.response?.data?.errorMessage?.includes('kindCode') ||
                              err?.message?.includes('kindCode');
      
      if (!is404 && !isKindCodeError) {
        rateLog('reminder-save-task-error', 'error', 15000, '❌ [GlobalReminderPopup] Failed to save reminder as task:', err);
      }
      
      // Try to fetch existing taskId as fallback
      const fallbackId = await fetchReminderTaskId();
      if (fallbackId) {
        return { task: { _id: fallbackId } };
      }
      
      // If kindCode error, log it but don't fail completely (reminder can work without task)
      if (isKindCodeError) {
        rateLog('reminder-kindcode-error', 'warn', 15000, '⚠️ [GlobalReminderPopup] kindCode required but not available for reminder - continuing without task');
      }
      
      return null;
    }
  }, [baseUrl, company]);

  const scheduleReminder = useCallback(async (task: Task) => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Use reminderTargetMs if available, otherwise fall back to triggerAt
    let targetMs: number;
    if (task.reminderTargetMs) {
      targetMs = task.reminderTargetMs;
    } else if (task.triggerAt) {
      targetMs = new Date(task.triggerAt).getTime();
    } else {
      rateLog('reminder-no-target-time', 'warn', 15000, 'No target time found for reminder');
      return;
    }

    const now = Date.now();
    const delta = targetMs - now;

    // Check if already delivered (prevent double-firing)
    if (task.reminderDeliveredAtMs && task.reminderDeliveredAtMs >= targetMs) {
      rateLog('reminder-already-delivered', 'log', 15000, 'Reminder already delivered, skipping');
      return;
    }

    // CRITICAL: Use reminderId for dedupe (not taskId) so new reminders for same task can trigger
    // Get reminderId from task object (may be in reminderId field or _id if __fromReminder)
    const reminderId = (task as any).reminderId || ((task as any).__fromReminder ? String(task._id) : null) || String(task._id);
    
    // CRITICAL FIX: Check if reminder was already shown BEFORE checking lock
    // This prevents infinite loops where popup keeps trying to show
    if (isReminderShown(reminderId, targetMs)) {
      // Check if popup is actually showing - if not, skip entirely (don't show again)
      const currentPopup = reminderPopupRef.current;
      const currentPopupTaskId = currentPopup?.taskId;
      const currentPopupReminderId = (currentPopup as any)?.reminderId;
      const matchesTaskId = currentPopupTaskId && String(currentPopupTaskId) === String(task._id);
      const matchesReminderId = currentPopupReminderId && String(currentPopupReminderId) === String(reminderId);
      
      if (!matchesTaskId && !matchesReminderId) {
        // Already shown and popup is not currently showing - skip entirely
        rateLog('reminder-already-shown-skip', 'log', 15000, 'Reminder already shown and popup not displaying, skipping:', reminderId);
        return;
      }
    }
    
    // Check multi-tab lock - use reminderId
    // CRITICAL FIX: Only check lock if reminder wasn't already shown
    const lockKey = `reminder-lock-${reminderId}`;
    const lockValue = localStorage.getItem(lockKey);
    if (lockValue && parseInt(lockValue, 10) >= targetMs) {
      // Check if popup is actually showing - if not, show it anyway
      const currentPopup = reminderPopupRef.current;
      const currentPopupTaskId = currentPopup?.taskId;
      const currentPopupReminderId = (currentPopup as any)?.reminderId;
      const matchesTaskId = currentPopupTaskId && String(currentPopupTaskId) === String(task._id);
      const matchesReminderId = currentPopupReminderId && String(currentPopupReminderId) === String(reminderId);
      
      if (matchesTaskId || matchesReminderId) {
        // Popup is already showing, skip
        rateLog('reminder-other-tab-handling', 'log', 15000, 'Another tab is handling this reminder and popup is showing');
        return;
      } else {
        // Lock exists but popup isn't showing - clear lock (will show below)
        console.warn('🔔 [GlobalReminderPopup] ⚠️ Lock exists but popup not showing - clearing lock:', reminderId);
        localStorage.removeItem(lockKey);
      }
    }
    
    const showPopup = async () => {
      // Don't show popup if user is not logged in
      if (!clientData || !clientData._id) {
        rateLog('reminder-user-not-logged-in', 'log', 15000, '🔔 [GlobalReminderPopup] User not logged in, skipping popup');
        return;
      }

      // Check if this reminder was marked as completed in localStorage - use reminderId
      const completedKey = `reminder-completed-${reminderId}`;
      const isCompletedInStorage = localStorage.getItem(completedKey);
      if (isCompletedInStorage) {
        rateLog('reminder-marked-completed', 'log', 15000, '🔔 [GlobalReminderPopup] Reminder marked as completed, never show again:', reminderId);
        return;
      }

      // Set lock - use reminderId
      localStorage.setItem(lockKey, String(targetMs));

      // If this originated from the reminders service, ensure a backing task exists first
      let taskIdForUpdate = task._id;
      let ensuredTaskMeta: any = null;
      if ((task as any).__fromReminder) {
        try {
          ensuredTaskMeta = await ensureReminderTask(String(task._id));
          if (ensuredTaskMeta?.task?._id) {
            taskIdForUpdate = ensuredTaskMeta.task._id;
          }
        } catch (err) {
          rateLog('reminder-ensure-task-error', 'error', 15000, 'Failed to ensure reminder task exists:', err);
        }
      }

      let updatedViaTaskEndpoint = false;
      if (!(task as any).__fromReminder || (taskIdForUpdate && taskIdForUpdate !== task._id)) {
        try {
          const rolePrefix = isAdmin ? 'admin' : 'manager';
          if (rolePrefix === 'admin') {
            await updateAdminTask(company, taskIdForUpdate, {
              reminderDeliveredAtMs: targetMs,
              bellActive: true, // Set bell to GREEN when popup shows
              reminderStatus: 'delivered' // Update status to delivered
            });
          } else {
            await updateManagerTask(company, taskIdForUpdate, {
              reminderDeliveredAtMs: targetMs,
              bellActive: true, // Set bell to GREEN when popup shows
              reminderStatus: 'delivered' // Update status to delivered
            });
          }
          updatedViaTaskEndpoint = true;
          rateLog('reminder-bell-active-set', 'log', 15000, '🔔 [GlobalReminderPopup] Set bellActive=true for task:', taskIdForUpdate);
        } catch (err) {
          rateLog('reminder-mark-delivered-task-error', 'error', 15000, 'Failed to mark reminder as delivered via task endpoint:', err);
        }
      }

      if (!updatedViaTaskEndpoint) {
        const reminderId =
          (task as any).__fromReminder
            ? String(task._id)
            : String((task as any).reminderId || ensuredTaskMeta?.reminder?._id || task._id);
        try {
          await updateReminder(company, reminderId, {
            reminderDeliveredAtMs: targetMs,
            bellActive: true,
            reminderStatus: 'delivered',
            taskId: taskIdForUpdate,
          });
          rateLog('reminder-mark-delivered-api', 'log', 15000, '🔔 [GlobalReminderPopup] Marked reminder as delivered via reminders API:', reminderId);
        } catch (fallbackErr) {
          rateLog('reminder-mark-delivered-api-error', 'error', 15000, 'Failed to mark reminder as delivered via reminders API:', fallbackErr);
        }
      }

      // Broadcast to other tabs - include reminderId and tabId
      // CRITICAL FIX: Include tabId so same tab doesn't close its own popup
      if (broadcastChannelRef.current) {
        const tabId = sessionStorage.getItem('tabId') || 'default';
        const reminderKey = `reminder-${reminderId}-${targetMs}`;
        tabReminderShownRef.current.add(reminderKey);
        
        broadcastChannelRef.current.postMessage({
          type: 'popup-shown',
          taskId: task._id,
          reminderId: reminderId, // Include reminderId for cross-tab dedupe
          targetMs,
          tabId // Include tabId to prevent self-closing
        });
      }

      // CRITICAL: Check for duplicates before showing popup - check both memory and localStorage
      // CRITICAL FIX: Only skip if popup is actually showing, not just marked as shown
      if (isReminderShown(reminderId, targetMs)) {
        // Check if popup is actually showing - if not, show it anyway
        const currentPopup = reminderPopupRef.current;
        const currentPopupTaskId = currentPopup?.taskId;
        const currentPopupReminderId = (currentPopup as any)?.reminderId;
        const matchesTaskId = currentPopupTaskId && String(currentPopupTaskId) === String(task._id);
        const matchesReminderId = currentPopupReminderId && String(currentPopupReminderId) === String(reminderId);
        
        if (matchesTaskId || matchesReminderId) {
          // Popup is already showing, skip
          return;
        } else {
          // Marked as shown but popup isn't showing - clear mark and show popup
          console.warn('🔔 [GlobalReminderPopup] ⚠️ Reminder marked as shown but popup not showing - clearing mark and showing popup:', reminderId);
          const reminderKey = `reminder-${reminderId}-${targetMs}`;
          shownRemindersRef.current.delete(reminderKey);
          localStorage.removeItem(`reminder-shown-${reminderId}`);
        }
      }
      
      // CRITICAL FIX: Mark as shown PERMANENTLY (so it doesn't show again even if popup is closed)
      markReminderAsShown(reminderId, targetMs);
      
      // Show popup
      const popupData = {
        taskId: task._id,
        title: task.title || 'תזכורת',
        description: task.description,
        deadline: task.deadline || null,
        targetMs,
        timeZone: task.reminderTimeZone || userTimeZone,
        isoLocal: task.reminderIsoLocal
      };
      
      
      // CRITICAL FIX: Update popup state IMMEDIATELY - update ref first, then state
      // This ensures the popup appears instantly without any delays
      reminderPopupRef.current = popupData;
      previousPopupRef.current = popupData;
      setReminderPopup(popupData);
      

      // Update bell status (dispatch event for ClientProfile)
      // Extract note text from description to keep bell green
      let noteText: string | null = null;
      if (task.description && task.description.startsWith('תזכורת להערה:')) {
        noteText = task.description.replace('תזכורת להערה:', '').trim();
      }
      window.dispatchEvent(new CustomEvent('reminder-popup-shown', {
        detail: { taskId: task._id, noteText }
      }));

      if ((task as any).__fromReminder) {
        // This is from reminders collection, so ensure task exists
        ensureReminderTask(String(task._id), false);
      }
    };

    // CRITICAL FIX: Only show immediately if the reminder time has actually arrived (past or very close)
    // For future reminders, always schedule them - don't show immediately
    // Use 500ms threshold to account for timing delays, but only for past/current times
    if (delta <= 500 && delta >= -60000) {
      // Time has arrived (within 500ms) or is in the past (up to 60 seconds) - show immediately
      showPopup().catch(err => {
        errorRateLimited('reminder-popup-error', () => `🔔 [GlobalReminderPopup] Error showing popup: ${err?.message || err}`);
      });
    } else if (delta > 500) {
      // Schedule for exact time (more than 60 seconds away)
      // Use shorter intervals when approaching the time for better accuracy
      const scheduleDelay = delta > 300000 ? 60000 : Math.min(delta, 30000); // Check every 30 seconds if less than 5 minutes away
      timeoutRef.current = setTimeout(() => {
        // Re-check if it's actually time
        const now = Date.now();
        const newDelta = targetMs - now;
        // CRITICAL FIX: Only show when time has actually arrived (within 500ms) or is in the past
        // Do NOT show for future reminders
        if (newDelta <= 500 && newDelta >= -60000) {

          showPopup().catch(err => {
            errorRateLimited('reminder-popup-timeout-error', () => `🔔 [GlobalReminderPopup] Error showing popup from timeout: ${err?.message || err}`);
          });
        } else if (newDelta > 60000) {
          // Still more than 60 seconds away, reschedule
          scheduleReminder(task);
        } else {
          showPopup().catch(err => {
            errorRateLimited('reminder-popup-past-due-error', () => `🔔 [GlobalReminderPopup] Error showing past due popup: ${err?.message || err}`);
          });
        }
      }, scheduleDelay);
    } else {

      showPopup().catch(err => {
        errorRateLimited('reminder-popup-error', () => `🔔 [GlobalReminderPopup] Error showing popup: ${err?.message || err}`);
      });
    }
  }, [company, isAdmin, baseUrl, userTimeZone, clientData, ensureReminderTask]);

  const fetchReminders = useCallback(async (): Promise<{ success: boolean; error?: any; upcomingReminders?: any[] }> => {
    // CRITICAL: Skip if company is not available (e.g., on root path or login page)
    if (!company) {
      return { success: true }; // Return success to avoid retry loops
    }

    // CRITICAL: Skip if user is not authenticated (no token)
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    if (!token) {
      return { success: true }; // Return success to avoid retry loops
    }

    // CRITICAL: Skip if network gate is active (network down or tab hidden)
    if (shouldSkipNetwork()) {
      return { success: true }; // Return success to avoid retry loops
    }

    if (isLocalApiButRemoteClient) {
      warnRateLimited('local-api-remote-client', () => '⚠️ [GlobalReminderPopup] Skipping reminder heartbeat because API host is localhost but app runs remotely.');
      return { success: true };
    }

    const now = Date.now();
    let attempted = false;
    let anySuccess = false;
    let lastError: any = null;
    let upcomingReminders: any[] = []; // Track reminders due within next 2 minutes

    const apiState = reminderApiStateRef.current;
    if (now >= apiState.skipUntil) {
      attempted = true;
      try {
        const remindersRes = await getUserReminders(company);
        const reminders = remindersRes?.reminders || [];
        const instantNow = Date.now();
        for (const reminder of reminders) {
          // CRITICAL: Exclude completed reminders - check multiple indicators
          // 1. Check reminderStatus
          if (reminder.reminderStatus === 'completed') continue;
          // 2. Check completedAtMs
          if (reminder.completedAtMs) continue;
          // 3. Check bellActive (if false, reminder is inactive/completed)
          if (!reminder?.bellActive) continue;
          // 4. Check localStorage (user marked as completed)
          const completedKey = `reminder-completed-${reminder._id}`;
          if (localStorage.getItem(completedKey)) continue;
          
          if (!reminder.reminderTargetMs) continue;

          const targetMs = reminder.reminderTargetMs;
          const delta = targetMs - instantNow;
          
          // Track reminders due within next 2 minutes for adaptive polling
          if (delta > 0 && delta <= 120000) {
            upcomingReminders.push({ reminder, targetMs, delta });
          }

          // CRITICAL FIX: Check if already shown (prevent infinite display) - check both memory and localStorage
          if (isReminderShown(String(reminder._id), targetMs)) {
            continue; // Already shown, skip
          }
          
          // CRITICAL FIX: Check if reminder recently failed (prevent popup from reappearing immediately after error)
          const reminderIdStr = String(reminder._id);
          const failedTime = failedRemindersRef.current.get(reminderIdStr);
          if (failedTime && (Date.now() - failedTime) < 30000) {
            // Failed within last 30 seconds - don't show again to prevent spam
            continue;
          }
          
          // CRITICAL FIX: Also check if popup is currently showing for this reminder
          const currentPopupTaskId = reminderPopup?.taskId;
          if (currentPopupTaskId && String(currentPopupTaskId) === String(reminder._id)) {
            continue; // Already showing, skip
          }

          // CRITICAL FIX: Only show when reminder time has actually arrived (past or very close)
          // Show if time has passed (delta <= 0) or is within 500ms of now (to account for timing delays)
          // Do NOT show for future reminders - they should be scheduled, not shown immediately
          if (delta <= 500 && delta >= -60000) { // Allow up to 60 seconds in the past, but only 500ms in the future
            // Note: Duplicate checks already done above
            // CRITICAL FIX: Mark as shown PERMANENTLY (so it doesn't show again even if popup is closed)
            markReminderAsShown(String(reminder._id), targetMs);
            
            const popupData = {
              taskId: String(reminder._id),
              title: reminder.title || 'תזכורת',
              description: reminder.noteText || '',
              deadline: new Date(targetMs).toISOString(),
              targetMs,
              timeZone: reminder.reminderTimeZone || userTimeZone,
              isoLocal: reminder.reminderIsoLocal
            };
            
            // Use helper to update with shallow compare
            updateReminderPopup(popupData);
            
            // This is from reminders collection, so ensure task exists
            ensureReminderTask(String(reminder._id), false);
            anySuccess = true;
            break;
          } else if (delta > 60000) {
            // Schedule for future (more than 60 seconds away)
            logRateLimited('reminder-schedule-future', () => `🔔 [GlobalReminderPopup] Scheduling reminder for future: ${reminder.title} in ${Math.round(delta / 1000)} seconds`);
            const taskLike: Task = {
              _id: String(reminder._id),
              title: reminder.title,
              description: reminder.noteText,
              reminderTargetMs: targetMs,
              reminderTimeZone: reminder.reminderTimeZone || userTimeZone,
              reminderIsoLocal: reminder.reminderIsoLocal,
              deadline: new Date(targetMs).toISOString(),
              isCalendar: true,
              status: '01',
              managerId: reminder.createdBy?._id || reminder.clientId,
              __fromReminder: true
            } as Task;
            scheduleReminder(taskLike);
            anySuccess = true;
          } else {
            // Between 5 seconds and 60 seconds - show immediately (approaching time)
            // CRITICAL FIX: Mark as shown BEFORE showing popup to prevent duplicates
            markReminderAsShown(String(reminder._id), targetMs);
            
            const popupData = {
              taskId: String(reminder._id),
              title: reminder.title || 'תזכורת',
              description: reminder.noteText || '',
              deadline: new Date(targetMs).toISOString(),
              targetMs,
              timeZone: reminder.reminderTimeZone || userTimeZone,
              isoLocal: reminder.reminderIsoLocal
            };

            
            updateReminderPopup(popupData);
            ensureReminderTask(String(reminder._id), false);
            anySuccess = true;
            break;
          }
          
          // OLD CODE - REMOVED: Past due handling moved above
          if (false) {
            // Past due (more than 5 seconds ago) - show anyway if not already shown
            const reminderKey = `reminder-${reminder._id}-${targetMs}`;
            if (shownRemindersRef.current.has(reminderKey)) {
              continue; // Already shown, skip
            }
            
            // CRITICAL FIX: Check if popup is currently showing for this reminder
            const currentPopupTaskId = reminderPopup?.taskId;
            if (currentPopupTaskId && String(currentPopupTaskId) === String(reminder._id)) {
              continue; // Already showing, skip
            }
            
            // CRITICAL FIX: Mark as shown BEFORE showing popup
            shownRemindersRef.current.add(reminderKey);
            localStorage.setItem(`reminder-shown-${reminder._id}`, String(targetMs));
            
            const popupData = {
              taskId: String(reminder._id),
              title: reminder.title || 'תזכורת',
              description: reminder.noteText || '',
              deadline: new Date(targetMs).toISOString(),
              targetMs,
              timeZone: reminder.reminderTimeZone || userTimeZone,
              isoLocal: reminder.reminderIsoLocal
            };
            

            
            // Use helper to update with shallow compare
            updateReminderPopup(popupData);
            // This is from reminders collection, so ensure task exists
            ensureReminderTask(String(reminder._id), false);
            anySuccess = true;
            break;
          }
        }
        // Mark network as up on success
        markNetworkUp();
        apiState.failCount = 0;
        // Use polling config interval
        apiState.skipUntil = Date.now() + getPollingInterval('reminders');
      } catch (err: any) {
        // Check if this is a network error - if so, mark network as down
        if (isNetworkError(err)) {
          markNetworkDown(30000, { key: 'reminders-api', reason: err?.message || 'Network error' });
        } else {
          markNetworkUp(); // Success on non-network errors means network is up
        }
        
        apiState.failCount = Math.min(apiState.failCount + 1, 6);
        apiState.skipUntil = Date.now() + Math.min(60000, 5000 * Math.pow(2, Math.max(0, apiState.failCount - 1)));
        lastError = err;
        
        // Only log non-connection errors (connection errors are expected when microservice is down)
        const isConnectionError = err?.code === 'ECONNREFUSED' || 
                                 err?.code === 'ENOTFOUND' ||
                                 err?.message?.includes('ECONNREFUSED') ||
                                 err?.message?.includes('ERR_CONNECTION_REFUSED') ||
                                 err?.response?.status === 503;
        
        if (!isConnectionError) {
          errorRateLimited('reminders-api-error', () => `Error loading reminders from new API: ${err?.message || err}`);
        }
      }
    }

    // REMOVED: Legacy REST fallback - RPC-only now
    // The RPC path above handles all cases. No fallback needed.

    if (!attempted) {
      return { success: true, upcomingReminders: [] };
    }

    if (!anySuccess && lastError) {
      return { success: false, error: lastError, upcomingReminders: [] };
    }

    return { success: true, upcomingReminders };
  }, [baseUrl, company, reminderPopup, userTimeZone, ensureReminderTask, isAdmin, scheduleReminder, clientData?._id, isLocalApiButRemoteClient, updateReminderPopup, isReminderShown, markReminderAsShown]);

  // Initialize BroadcastChannel for multi-tab coordination
  useEffect(() => {
    if (typeof BroadcastChannel !== 'undefined') {
      const channel = new BroadcastChannel(REMINDER_CHANNEL);
      broadcastChannelRef.current = channel;

      channel.onmessage = (event) => {
        // PERFORMANCE FIX: Use setTimeout(0) instead of Promise.resolve() to truly defer execution
        // This moves execution to the next event loop tick, preventing [Violation] warnings
        setTimeout(() => {
          const { type, taskId, targetMs, tabId, reminderId } = event.data;
          
          // CRITICAL FIX: Ignore messages from the same tab (prevent self-closing)
          const currentTabId = sessionStorage.getItem('tabId') || 'default';
          if (tabId === currentTabId) {
            return; // This message came from this tab, ignore it
          }
          
          // CRITICAL FIX: Only close if this is for the currently showing popup
          const currentPopupTaskId = reminderPopup?.taskId;
          const currentPopupReminderId = (reminderPopup as any)?.reminderId;
          const matchesTaskId = currentPopupTaskId && taskId && String(currentPopupTaskId) === String(taskId);
          const matchesReminderId = currentPopupReminderId && reminderId && String(currentPopupReminderId) === String(reminderId);
          
          if (type === 'popup-shown' && (matchesTaskId || matchesReminderId)) {
            // Another tab showed this popup, close ours
            startTransition(() => {
              updateReminderPopup(null);
              if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
              }
            });
          } else if (type === 'popup-dismissed' && (matchesTaskId || matchesReminderId)) {
            // Another tab dismissed, close ours
            startTransition(() => {
              updateReminderPopup(null);
            });
          } else {
          }
        }, 0);
      };

      return () => {
        channel.close();
        broadcastChannelRef.current = null;
      };
    }
  }, [reminderPopup?.taskId]);

  // Load pinned position and lock state from localStorage on mount
  useEffect(() => {
    const savedPosition = localStorage.getItem('reminder-popup-position');
    const savedPinned = localStorage.getItem('reminder-popup-pinned');
    
    if (savedPosition) {
      try {
        const pos = JSON.parse(savedPosition);
        // CRITICAL FIX: Validate position is within viewport
        if (typeof window !== 'undefined') {
          const maxX = window.innerWidth - 320; // 320px = w-80 width
          const maxY = window.innerHeight - 100;
          const validatedPos = {
            x: Math.max(0, Math.min(pos.x, maxX)),
            y: Math.max(0, Math.min(pos.y, maxY))
          };
          // Only set if position is valid (not off-screen)
          if (validatedPos.x >= 0 && validatedPos.y >= 0 && 
              validatedPos.x <= window.innerWidth && validatedPos.y <= window.innerHeight) {
            setPosition(validatedPos);
          } else {
            // Clear invalid position
            localStorage.removeItem('reminder-popup-position');
            console.warn('🔔 [GlobalReminderPopup] ⚠️ Invalid saved position, cleared:', pos);
          }
        } else {
          setPosition(pos);
        }
      } catch (err) {
        rateLog('reminder-position-parse-error', 'error', 15000, 'Error parsing saved position:', err);
        localStorage.removeItem('reminder-popup-position');
      }
    }
    
    if (savedPinned === 'true') {
      setIsPinned(true);
    }
  }, []);
  
  // Also restore position when popup appears (if it was pinned)
  useEffect(() => {
    if (reminderPopup && isPinned && position) {
      // Position will be applied via popupStyle, but ensure it's set
      // This is mainly for when popup appears after being hidden
    }
  }, [reminderPopup, isPinned, position]);

  // CRITICAL: Debug popup visibility when it renders
  useEffect(() => {
    if (reminderPopup && popupRef.current) {
      // Wait for next frame to ensure DOM is updated
      requestAnimationFrame(() => {
        if (popupRef.current) {
          const rect = popupRef.current.getBoundingClientRect();
          const computedStyle = window.getComputedStyle(popupRef.current);
          
          // If popup is off-screen, reset position
          if (rect.top < 0 || rect.left < 0 || 
              rect.bottom > window.innerHeight || 
              rect.right > window.innerWidth) {
            console.warn('🔔 [GlobalReminderPopup] ⚠️ Popup is off-screen, resetting position');
            setPosition(null);
            setIsPinned(false);
            localStorage.removeItem('reminder-popup-position');
            localStorage.removeItem('reminder-popup-pinned');
          }
        }
      });
    }
  }, [reminderPopup]);

  // Save position to localStorage whenever pinned state or position changes
  useEffect(() => {
    if (isPinned && position) {
      // Save both position and pinned state
      localStorage.setItem('reminder-popup-position', JSON.stringify(position));
      localStorage.setItem('reminder-popup-pinned', 'true');
    } else if (!isPinned) {
      // When unpinning, remove pinned flag but keep position for next time user pins
      localStorage.removeItem('reminder-popup-pinned');
      // Optionally, also save current position even when not pinned (for next time)
      if (position && popupRef.current) {
        const rect = popupRef.current.getBoundingClientRect();
        localStorage.setItem('reminder-popup-position', JSON.stringify({ x: rect.left, y: rect.top }));
      }
    }
  }, [isPinned, position]);

  // Save position when dragging ends (only if pinned)
  const savePosition = useCallback((x: number, y: number) => {
    const pos = { x, y };
    setPosition(pos);
    // Position will be saved to localStorage by the useEffect above if isPinned is true
  }, []);

  // Draggable handlers - only allow dragging when NOT pinned
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    // Don't allow dragging when locked (pinned)
    if (isPinned) return;
    
    if (!popupRef.current) return;
    setIsDragging(true);
    const rect = popupRef.current.getBoundingClientRect();
    dragStartRef.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  }, [isPinned]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    // Don't allow dragging when locked (pinned)
    if (isPinned) {
      setIsDragging(false);
      dragStartRef.current = null;
      return;
    }
    
    if (!isDragging || !dragStartRef.current || !popupRef.current) return;
    const x = e.clientX - dragStartRef.current.x;
    const y = e.clientY - dragStartRef.current.y;
    
    // Constrain to viewport
    const maxX = window.innerWidth - popupRef.current.offsetWidth;
    const maxY = window.innerHeight - popupRef.current.offsetHeight;
    const constrainedX = Math.max(0, Math.min(x, maxX));
    const constrainedY = Math.max(0, Math.min(y, maxY));
    
    popupRef.current.style.left = `${constrainedX}px`;
    popupRef.current.style.top = `${constrainedY}px`;
    // Save position during drag (will be persisted when user pins)
    savePosition(constrainedX, constrainedY);
  }, [isDragging, isPinned, savePosition]);

  const handleMouseUp = useCallback(() => {
    if (isPinned) {
      return; // Don't do anything if pinned
    }
    
    setIsDragging(false);
    dragStartRef.current = null;
    
    // Save final position when drag ends
    if (popupRef.current) {
      const rect = popupRef.current.getBoundingClientRect();
      savePosition(rect.left, rect.top);
    }
  }, [isPinned, savePosition]);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Heartbeat: re-arm missing timeouts (safety net for sleep/wake)
  // CRITICAL: Fixed with exponential backoff, network guards, and visibility checks
  useEffect(() => {
    // Skip reminders for superadmin (they don't have a company database)
    if (!company || !baseUrl || !clientData?._id || company === 'superadmin' || clientData?.role === 'superadmin') return;

    // Track heartbeat state with backoff
    const heartbeatState = {
      failCount: 0,
      lastSuccessTime: Date.now(),
      isPaused: false,
      backoffDelay: 5000, // Start with 5 seconds
      maxBackoffDelay: 60000, // Max 60 seconds
    };

    // Throttled error logger (max once per 10 seconds)
    let lastErrorLog = 0;
    const logHeartbeatError = (err: any, context: string) => {
      const now = Date.now();
      if (now - lastErrorLog < 10000) return; // Skip if within cooldown
      lastErrorLog = now;
      
      const isNetworkError = !err?.response;
      const isConnectionRefused = err?.code === 'ECONNREFUSED' || 
                                  err?.code === 'ERR_CONNECTION_REFUSED' ||
                                  err?.message?.includes('ERR_CONNECTION_REFUSED');
      
      if (isNetworkError || isConnectionRefused) {
        rateLog(`heartbeat-${context}`, 'warn', 15000, `[GlobalReminderPopup] Heartbeat ${context} - Network error (will backoff):`, {
          code: err?.code,
          message: err?.message?.substring(0, 100),
          failCount: heartbeatState.failCount,
          backoffDelay: heartbeatState.backoffDelay,
        });
      }
    };

    const heartbeat = async () => {
      // GUARD 1: Skip if network gate is active (network down or tab hidden)
      if (shouldSkipNetwork()) {
        return;
      }

      // GUARD 2: Skip if paused due to backoff
      if (heartbeatState.isPaused) {
        const timeSinceLastSuccess = Date.now() - heartbeatState.lastSuccessTime;
        if (timeSinceLastSuccess < heartbeatState.backoffDelay) {
          return; // Still in backoff period
        }
        // Backoff period expired, resume
        heartbeatState.isPaused = false;
      }

      // GUARD 3: Skip if company is not available
      if (!company) {
        return;
      }

      // GUARD 4: Skip if user is not authenticated
      const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
      if (!token) {
        return;
      }

      const legacyState = reminderLegacyStateRef.current;
      const nowTs = Date.now();
      if (nowTs < legacyState.skipUntil) {
        return;
      }

      // CRITICAL FIX: Defer heavy operations to prevent "[Violation] 'setInterval' handler took <N>ms"
      // Use setTimeout(0) to defer API calls and processing to next event loop tick
      setTimeout(async () => {
        try {
          const rolePrefix = isAdmin ? 'admin' : 'manager';
          // Use API helpers instead of building URLs
          let heartbeatSuccess = false;
          
          try {
            let tasks: Task[] = [];
            if (isAdmin) {
              const { getAdminTasks } = await import('../api/tasksApi');
              const adminTasksData = await getAdminTasks(company, {});
              tasks = adminTasksData?.tasks || adminTasksData || [];
            } else {
              const rpcManagers = (await import('../api/rpc/rpcManagers')).default;
              const managerTasksResponse = await rpcManagers.listTasks({});
              const managerTasksData = managerTasksResponse?.data;
              if (managerTasksData?.ok && managerTasksData.data) {
                tasks = managerTasksData.data.tasks || managerTasksData.data || [];
              }
            }
            
            // Process tasks - defer heavy filtering/processing if needed
            if (tasks.length > 0) {
              // Use requestIdleCallback if available for non-urgent processing
              const processTasks = () => {
                const activeReminders = tasks.filter((t: Task) => {
                  if (!t.isCalendar || !t.description?.startsWith('תזכורת להערה:')) return false;
                  if (!t.triggerAt && !t.reminderTargetMs) return false;
                  
                  // CRITICAL: Exclude completed reminders
                  if ((t as any).reminderStatus === 'completed') return false;
                  if ((t as any).completedAtMs) return false;
                  const completedKey = `reminder-completed-${t._id}`;
                  if (localStorage.getItem(completedKey)) return false;
                  if ((t as any).bellActive === false) return false;
                  
                  if (t.reminderSentAt) return false;
                  if (!['טיוטא', 'בטיפול', 'לטיפול', '01', '02', '05'].includes(t.status || '')) return false;

                  const managerId = typeof t.managerId === 'string' ? t.managerId : (t.managerId as any)?._id || '';
                  const managerIdMatch = String(managerId) === String(clientData._id);
                  const createdById = typeof t.createdBy === 'string' ? t.createdBy : (t.createdBy as TaskLiteUser)?._id || '';
                  const createdByMatch = String(createdById) === String(clientData._id);

                  return (managerIdMatch || createdByMatch);
                });

                // CRITICAL FIX: Check for reminders that are due NOW and show them immediately
                // Increased threshold to 60 seconds to ensure real-time reminders show immediately
                // This accounts for polling delays and ensures popup shows at exact time
                const now = Date.now();
                const dueNow = activeReminders.find(t => {
                  const targetMs = t.reminderTargetMs || (t.triggerAt ? new Date(t.triggerAt).getTime() : 0);
                  if (!targetMs || targetMs <= 0) return false;
                  const delta = targetMs - now;
                  // Show if due within last 60 seconds or next 60 seconds (to catch any that just became due or are about to)
                  return delta <= 60000 && delta >= -60000;
                });

                if (dueNow) {
                  // Show immediately - don't wait for timeout
                  const targetMs = dueNow.reminderTargetMs || (dueNow.triggerAt ? new Date(dueNow.triggerAt).getTime() : 0);
                  // CRITICAL: Use reminderId for dedupe (get from task.reminderId or task._id if __fromReminder)
                  const taskReminderId = (dueNow as any).reminderId || ((dueNow as any).__fromReminder ? String(dueNow._id) : null) || String(dueNow._id);
                  const completedKey = `reminder-completed-${taskReminderId}`;
                  if (!localStorage.getItem(completedKey)) {
                    const reminderKey = `reminder-${taskReminderId}-${targetMs}`;
                    if (!shownRemindersRef.current.has(reminderKey)) {
                      scheduleReminder(dueNow);
                    }
                  }
                }

                // Schedule the next upcoming reminder (for future)
                if (activeReminders.length > 0) {
                  const upcoming = activeReminders
                    .map(t => ({
                      task: t,
                      targetMs: t.reminderTargetMs || (t.triggerAt ? new Date(t.triggerAt).getTime() : Infinity)
                    }))
                    .filter(({ task, targetMs }) => {
                      if (targetMs <= now) return false; // Skip past due (already handled above)
                      if (task.reminderDeliveredAtMs && task.reminderDeliveredAtMs >= targetMs) return false;
                      return true;
                    })
                    .sort((a, b) => a.targetMs - b.targetMs)[0];

                  if (upcoming && !timeoutRef.current) {
                    // Only schedule if no timeout exists (to avoid conflicts)
                    scheduleReminder(upcoming.task);
                  }
                }
                heartbeatSuccess = true;
              };

              // Use requestIdleCallback if available, otherwise setTimeout
              if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
                (window as any).requestIdleCallback(processTasks, { timeout: 1000 });
              } else {
                setTimeout(processTasks, 0);
              }
            }
          } catch (err: any) {
            // Check for network errors
            const isNetworkError = !err?.response;
            const isConnectionRefused = err?.code === 'ECONNREFUSED' || 
                                        err?.code === 'ERR_CONNECTION_REFUSED' ||
                                        err?.message?.includes('ERR_CONNECTION_REFUSED');
            
            if (isNetworkError || isConnectionRefused) {
              // Network error - mark network as down and apply exponential backoff
              markNetworkDown(30000, { key: 'heartbeat', reason: err?.message || 'Network error' });
              heartbeatState.failCount++;
              heartbeatState.backoffDelay = Math.min(
                heartbeatState.maxBackoffDelay,
                5000 * Math.pow(2, Math.min(heartbeatState.failCount - 1, 4)) // 5s, 10s, 20s, 40s, 60s max
              );
              heartbeatState.isPaused = true;
              heartbeatState.lastSuccessTime = Date.now();
              
              logHeartbeatError(err, 'network error');
            } else if (err?.response?.status !== 403) {
              // Non-network error (but not 403 permission error) - network is up
              markNetworkUp();
              logHeartbeatError(err, 'API error');
            }
          }
          
          if (heartbeatSuccess) {
            // Success - mark network as up and reset backoff
            markNetworkUp();
            heartbeatState.failCount = 0;
            heartbeatState.backoffDelay = 5000; // Reset to initial delay
            heartbeatState.isPaused = false;
            heartbeatState.lastSuccessTime = Date.now();
            legacyState.failCount = 0;
            legacyState.skipUntil = Date.now() + getPollingInterval('heartbeat');
          } else {
            // No success but not a network error - use legacy backoff
            legacyState.failCount = Math.min(legacyState.failCount + 1, 6);
            legacyState.skipUntil = Date.now() + Math.min(30000, 5000 * Math.pow(2, Math.max(0, legacyState.failCount - 1)));
          }
        } catch (err: any) {
          // Outer catch - network errors
          const isNetworkError = !err?.response;
          const isConnectionRefused = err?.code === 'ECONNREFUSED' || 
                                      err?.code === 'ERR_CONNECTION_REFUSED' ||
                                      err?.message?.includes('ERR_CONNECTION_REFUSED');
          
          if (isNetworkError || isConnectionRefused) {
            // Network error - mark network as down
            markNetworkDown(30000, { key: 'heartbeat-outer', reason: err?.message || 'Network error' });
            heartbeatState.failCount++;
            heartbeatState.backoffDelay = Math.min(
              heartbeatState.maxBackoffDelay,
              5000 * Math.pow(2, Math.min(heartbeatState.failCount - 1, 4))
            );
            heartbeatState.isPaused = true;
            heartbeatState.lastSuccessTime = Date.now();
            
            logHeartbeatError(err, 'outer catch');
          } else {
            // Non-network error - network is up
            markNetworkUp();
            logHeartbeatError(err, 'outer catch non-network');
          }
          
          legacyState.failCount = Math.min(legacyState.failCount + 1, 6);
          legacyState.skipUntil = Date.now() + Math.min(30000, 5000 * Math.pow(2, Math.max(0, legacyState.failCount - 1)));
        }
      }, 0);
    };

    // Use controlled interval with dynamic delay based on backoff
    let intervalId: NodeJS.Timeout | null = null;
    
    const scheduleNextHeartbeat = () => {
      if (intervalId) {
        clearTimeout(intervalId);
      }
      
      const delay = heartbeatState.isPaused 
        ? heartbeatState.backoffDelay 
        : getPollingInterval('heartbeat'); // Use polling config
      
      intervalId = setTimeout(() => {
        heartbeat();
        scheduleNextHeartbeat(); // Schedule next heartbeat
      }, delay);
    };

    // Initial heartbeat
    heartbeat();
    scheduleNextHeartbeat();

    // Listen for online/offline events
    const handleOnline = () => {
      markNetworkUp(); // Mark network as recovered
      heartbeatState.isPaused = false;
      heartbeatState.failCount = 0;
      heartbeatState.backoffDelay = 5000;
      heartbeat(); // Resume immediately
    };

    const handleOffline = () => {
      markNetworkDown(30000, { key: 'heartbeat-offline', reason: 'Browser offline' });
      heartbeatState.isPaused = true;
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Tab became visible - resume heartbeat (networkGate will handle visibility)
        heartbeatState.isPaused = false;
        heartbeat();
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    return () => {
      if (intervalId) {
        clearTimeout(intervalId);
        intervalId = null;
      }
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
      if (typeof window !== 'undefined') {
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
    };
  }, [company, baseUrl, isAdmin, clientData?._id, scheduleReminder]);

  // Handle reminder-popup events from the reminder worker
  // CRITICAL: Declare this BEFORE handleTaskReminderNotification so it can be used there
  const handleReminderPopup = useCallback(async (payload: any) => {
    // Skip for superadmin
    if (!clientData?._id || !company || company === 'superadmin' || clientData?.role === 'superadmin') return;
    
    rateLog('reminder-popup-event-received', 'log', 15000, '🔔 [GlobalReminderPopup] Received reminder-popup event:', payload);
    // CRITICAL: Extract reminderId from payload (can be in reminderId, refId, or meta.reminderId)
    const reminderId = payload?.reminderId || payload?.refId || payload?.meta?.reminderId;
    if (reminderId) {
      try {
        // Fetch reminder from the new Reminder API
        const reminderRes = await getReminder(company, reminderId);
        const reminder = (reminderRes as any)?.reminder;
        
        if (reminder && reminder.reminderTargetMs) {
          const targetMs = reminder.reminderTargetMs;
          const now = Date.now();
          
          // CRITICAL FIX: Only show immediately if the reminder time has actually arrived (past or very close)
          // For future reminders, always schedule them - don't show immediately
          // Use 500ms threshold to account for timing delays, but only for past/current times
          const delta = targetMs - now;
          // Only show immediately if time has passed (delta <= 0) or is within 500ms of now
          // This prevents future reminders from showing immediately when created
          if (delta <= 500 && delta >= -60000) { // Allow up to 60 seconds in the past, but only 500ms in the future
            // CRITICAL: Prevent duplicate popups - check if this reminder was already shown (memory + localStorage)
            if (isReminderShown(reminderId, targetMs)) {
              return;
            }
            
            // CRITICAL FIX: Check if popup is currently showing for this reminder
            if (reminderPopup && String(reminderPopup.taskId) === String(reminderId)) {
              return;
            }
            
            // Check if already completed
            const completedKey = `reminder-completed-${reminderId}`;
            if (localStorage.getItem(completedKey)) {
              return;
            }
            
            // CRITICAL FIX: Mark as shown PERMANENTLY BEFORE showing popup (so it doesn't show again even if closed)
            markReminderAsShown(reminderId, targetMs);
            
            
            const popupData = {
              taskId: reminder.taskId || reminderId, // Use taskId if available, otherwise reminderId
              reminderId: reminderId, // CRITICAL: Store reminderId for dedupe
              title: reminder.title || 'תזכורת',
              description: reminder.noteText || '',
              deadline: new Date(targetMs).toISOString(),
              targetMs: targetMs,
              timeZone: reminder.reminderTimeZone || userTimeZone,
              isoLocal: reminder.reminderIsoLocal
            };
            
            // CRITICAL FIX: Always update popup state IMMEDIATELY (don't check for existing - that prevents showing)
            // Update refs first for immediate access, then state
            reminderPopupRef.current = popupData;
            previousPopupRef.current = popupData;
            setReminderPopup(popupData);
            
            
            // This is from reminders collection, so ensure task exists (async, don't block popup)
            ensureReminderTask(String(reminderId), false).catch(err => {
              rateLog('reminder-ensure-task-error-handle', 'error', 15000, 'Failed to ensure reminder task:', err);
            });
          } else {
            // Schedule for future - use scheduleReminder to set up the timeout
            logRateLimited('reminder-schedule-future-event', () => `🔔 [GlobalReminderPopup] Scheduling reminder for future: ${Math.round((targetMs - now) / 1000 / 60)} minutes`);
            // Create a task-like object for scheduleReminder
            const taskLike = {
              _id: reminderId,
              title: reminder.title,
              description: reminder.noteText,
              reminderTargetMs: targetMs,
              reminderTimeZone: reminder.reminderTimeZone || userTimeZone,
              reminderIsoLocal: reminder.reminderIsoLocal,
              deadline: new Date(targetMs).toISOString(),
              isCalendar: true,
              status: '01',
              managerId: reminder.createdBy?._id || reminder.clientId,
              __fromReminder: true // Indicate it's from a reminder
            } as Task & { reminderId?: string; __fromReminder?: boolean };
            taskLike.reminderId = reminderId;
            scheduleReminder(taskLike);
          }
        }
      } catch (err) {
        rateLog('reminder-fetch-error', 'error', 15000, '❌ [GlobalReminderPopup] Error fetching reminder:', err);
        // Fallback: use payload data directly (if reminder fetch failed)
        if (payload.reminderTargetMs || payload.targetMs || payload.deadline) {
          const targetMs = payload.reminderTargetMs || payload.targetMs || (payload.deadline ? new Date(payload.deadline).getTime() : Date.now());
          
          // CRITICAL: Check dedupe before showing popup
          const reminderKey = `reminder-${reminderId}-${targetMs}`;
          if (shownRemindersRef.current.has(reminderKey)) {
            rateLog('reminder-already-shown-fallback', 'log', 15000, '🔔 [GlobalReminderPopup] Reminder already shown (fallback), skipping:', reminderId);
            return;
          }
          
          const completedKey = `reminder-completed-${reminderId}`;
          if (localStorage.getItem(completedKey)) {
            rateLog('reminder-already-completed-fallback', 'log', 15000, '🔔 [GlobalReminderPopup] Reminder already completed (fallback), skipping:', reminderId);
            return;
          }
          
          // Mark as shown
          shownRemindersRef.current.add(reminderKey);
          localStorage.setItem(`reminder-shown-${reminderId}`, String(targetMs));
          
          const popupData = {
            taskId: payload.taskId || reminderId,
            reminderId: reminderId, // CRITICAL: Store reminderId for dedupe
            title: payload.title || 'תזכורת',
            description: payload.noteText || payload.text || '',
            deadline: payload.deadline ? new Date(payload.deadline).toISOString() : new Date(targetMs).toISOString(),
            targetMs: targetMs,
            timeZone: payload.reminderTimeZone || payload.timeZone || userTimeZone,
            isoLocal: payload.reminderIsoLocal
          };
          
          // CRITICAL FIX: Update popup state IMMEDIATELY (don't use updateReminderPopup - it might have delays)
          reminderPopupRef.current = popupData;
          previousPopupRef.current = popupData;
          setReminderPopup(popupData);
          
        }
      }
    }
  }, [clientData?._id, clientData?.role, company, userTimeZone, scheduleReminder, ensureReminderTask]);

  // Handle task_reminder notifications (using centralized socketManager)
  // CRITICAL: Declare AFTER handleReminderPopup so it can reference it, but BEFORE useSocketSubscription
  const handleTaskReminderNotification = useCallback(async (payload: any) => {
    // Skip for superadmin
    if (!clientData?._id || !company || company === 'superadmin' || clientData?.role === 'superadmin') return;
    
    // CRITICAL: Handle both 'reminder' and 'task_reminder' types
    // 'reminder' type comes from new Reminder collection (has reminderId)
    // 'task_reminder' type comes from legacy Task collection
    if ((payload?.type === 'reminder' || payload?.type === 'task_reminder') && (payload?.reminderId || payload?.refId)) {
      // CRITICAL FIX: Extract reminderId and check if already shown (prevent infinite display)
      const reminderId = payload?.reminderId || payload?.refId || payload?.meta?.reminderId;
      if (reminderId) {
        const targetMs = payload?.reminderTargetMs || payload?.targetMs || (payload?.deadline ? new Date(payload.deadline).getTime() : Date.now());
        const reminderKey = `reminder-${reminderId}-${targetMs}`;
        
        // CRITICAL FIX: Check if popup is currently showing for this reminder FIRST
        // Only skip if popup is actually showing, not just marked as shown
        const currentPopup = reminderPopupRef.current;
        if (currentPopup) {
          const currentTaskId = String(currentPopup.taskId);
          const currentReminderId = String((currentPopup as any)?.reminderId || currentPopup.taskId);
          if (currentTaskId === String(reminderId) || currentReminderId === String(reminderId)) {
            return;
          }
        }
        
        // CRITICAL FIX: Check if already shown (prevent infinite display) - but only skip if popup is actually showing
        if (isReminderShown(reminderId, targetMs)) {
          // Check again if popup is actually showing
          if (!currentPopup || (String(currentPopup.taskId) !== String(reminderId) && String((currentPopup as any)?.reminderId || currentPopup.taskId) !== String(reminderId))) {
            // Marked as shown but popup isn't showing - clear mark and allow showing
            const reminderKey = `reminder-${reminderId}-${targetMs}`;
            shownRemindersRef.current.delete(reminderKey);
            localStorage.removeItem(`reminder-shown-${reminderId}`);
          } else {
            return;
          }
        }
        
        // CRITICAL FIX: Mark as shown PERMANENTLY BEFORE processing (so it doesn't show again even if closed)
        markReminderAsShown(reminderId, targetMs);
      }
      
      // For 'reminder' type, use handleReminderPopup which properly handles reminderId
      if (payload?.type === 'reminder' && (payload?.reminderId || payload?.refId)) {
        // Delegate to handleReminderPopup which has correct dedupe logic
        handleReminderPopup(payload);
        return;
      }
      
      // For 'task_reminder' type (legacy), fetch task and schedule
      if (payload?.type === 'task_reminder' && payload?.refId) {
        try {
          let task: Task | null = null;
          try {
            if (isAdmin) {
              const taskRes = await getAdminTaskById(company, payload.refId);
              task = (taskRes as any)?.task || taskRes;
            } else {
              const taskRes = await getManagerTaskById(company, payload.refId);
              task = (taskRes as any)?.task || taskRes;
            }
          } catch {
            const taskRes = await getAdminTaskById(company, payload.refId);
            task = (taskRes as any)?.task || taskRes;
          }

          if (task) {
            scheduleReminder(task);
          }
        } catch (err) {
          rateLog('reminder-task-fetch-error', 'error', 15000, 'Error fetching reminder task:', err);
        }
      }
    }
  }, [clientData?._id, clientData?.role, company, isAdmin, scheduleReminder, handleReminderPopup]);

  // Handle show-reminder-popup custom event (from AdminTasksPage when creating reminder)
  useEffect(() => {    
    const handleShowReminderPopup = async (event: Event) => {
      const customEvent = event as CustomEvent;
      const payload = customEvent.detail;
      
      
      if (!payload || !payload.taskId) {
        return;
      }
      
      const { taskId, title, description, deadline, targetMs, timeZone, isoLocal, reminderId: payloadReminderId } = payload;
      const now = Date.now();
      
      // CRITICAL: Use reminderId from payload if available, otherwise fallback to taskId
      // For new reminders created after completion, payload should have reminderId
      const reminderId = payloadReminderId || taskId;
      
      // CRITICAL FIX: Only show immediately if the reminder time has actually arrived (past or very close)
      // For future reminders, always schedule them - don't show immediately
      // Use 500ms threshold to account for timing delays, but only for past/current times
      const delta = targetMs ? targetMs - now : Infinity;
      // Only show immediately if time has passed (delta <= 0) or is within 500ms of now
      // This prevents future reminders from showing immediately when created
      // delta <= 500 means time has arrived or is very close (within 500ms - accounts for timing delays)
      // delta >= -60000 means not too far in the past (within 60 seconds)
      const shouldShowImmediately = targetMs && delta <= 500 && delta >= -60000;
      
      if (shouldShowImmediately) {
        // CRITICAL: Check if already shown (prevent infinite display) - check both memory and localStorage
        if (isReminderShown(reminderId, targetMs)) {
          return;
        }
        
        // Check if already completed
        const completedKey = `reminder-completed-${reminderId}`;
        if (localStorage.getItem(completedKey)) {
          return;
        }
        
        // CRITICAL FIX: Mark as shown PERMANENTLY BEFORE showing popup (so it doesn't show again even if closed)
        markReminderAsShown(reminderId, targetMs);
        

        
        // Use helper to update with shallow compare
        const popupData = {
          taskId: String(taskId),
          reminderId: reminderId, // CRITICAL: Store reminderId for dedupe
          title: title || 'תזכורת',
          description: description || '',
          deadline: deadline || new Date(targetMs).toISOString(),
          targetMs: targetMs,
          timeZone: timeZone || userTimeZone,
          isoLocal: isoLocal
        };
        
        // CRITICAL: Force update immediately, don't check for existing popup
        updateReminderPopup(popupData);
        // CRITICAL FIX: Don't call ensureReminderTask for task-based reminders
        // Task-based reminders (from AdminTasksPage) are already tasks, not reminder documents
        // Only call ensureReminderTask for reminders from the reminders collection
        // (which have __fromReminder flag or come from reminder APIs)
        // This is a task-based reminder, so skip ensureReminderTask
      } else if (targetMs) {
        // Schedule for future
        rateLog('reminder-schedule-future-event', 'log', 15000, '🔔 [GlobalReminderPopup] Scheduling reminder for future from event:', Math.round((targetMs - now) / 1000 / 60), 'minutes');
        
        const taskLike = {
          _id: String(taskId),
          title: title || 'תזכורת',
          description: description || '',
          reminderTargetMs: targetMs,
          reminderTimeZone: timeZone || userTimeZone,
          reminderIsoLocal: isoLocal,
          deadline: deadline || new Date(targetMs).toISOString(),
          isCalendar: true,
          status: '01',
          bellActive: true,
          __fromReminder: false // This is a task-based reminder
        } as Task;
        
        scheduleReminder(taskLike);
      }
    };
    
    window.addEventListener('show-reminder-popup', handleShowReminderPopup as EventListener);
    
    return () => {
      window.removeEventListener('show-reminder-popup', handleShowReminderPopup as EventListener);
    };
  }, [userTimeZone, scheduleReminder, ensureReminderTask, isReminderShown, markReminderAsShown]);

  // Subscribe to socket events for reminders
  useSocketSubscription('notification', handleTaskReminderNotification);
  useSocketSubscription('reminder-popup', handleReminderPopup);
  useSocketSubscription('reminder:due', handleReminderPopup); // Primary event for reminder popups
  
  // CRITICAL: Polling fallback - scan notifications list for reminder notifications
  // This catches reminders if websocket missed them (e.g., tab was inactive, connection dropped)
  useEffect(() => {
    if (!company || !clientData?._id || company === 'superadmin' || clientData?.role === 'superadmin') return;
    
    let pollingInterval: NodeJS.Timeout | null = null;
    let lastCheckedAt = Date.now();
    
    const checkNotificationsForReminders = async () => {
      // CRITICAL: Skip if network gate is active
      if (shouldSkipNetwork()) {
        return;
      }
      
      try {
        const notificationsRes = await getNotifications(company);
        const notifications = (notificationsRes as any)?.notifications || [];
        
        // Scan last 20 notifications (most recent) for reminder types
        const recentNotifications = notifications.slice(0, 20);
        const now = Date.now();
        
        for (const notif of recentNotifications) {
          // Check if it's a reminder notification (handle both 'reminder' and 'REMINDER_DUE' types)
          if ((notif.type === 'reminder' || notif.type === 'REMINDER_DUE') && (notif.reminderId || notif.refId || notif.meta?.reminderId)) {
            // CRITICAL: Extract reminderId from multiple possible locations
            const reminderId = String(notif.reminderId || notif.refId || notif.meta?.reminderId);
            
            // Check if notification was created recently (within last 5 minutes)
            const notifCreatedAt = notif.createdAt ? new Date(notif.createdAt).getTime() : 0;
            if (notifCreatedAt < (now - 5 * 60 * 1000)) {
              continue; // Too old, skip
            }
            
            // Check if reminder is due (within last 5 minutes or next 5 minutes)
            const targetMs = notif.reminderTargetMs || (notif.deadline ? new Date(notif.deadline).getTime() : 0);
            if (targetMs <= 0) continue;
            
            const delta = targetMs - now;
            if (delta > 5 * 60 * 1000 || delta < -5 * 60 * 1000) continue; // Not due yet or too old
            
            // CRITICAL FIX: Check if we already showed this reminder (prevent infinite display) - check both memory and localStorage
            if (isReminderShown(reminderId, targetMs)) {
              continue; // Already shown, skip
            }
            
            // CRITICAL FIX: Check if popup is currently showing for this reminder
            if (reminderPopup && String(reminderPopup.taskId) === String(reminderId)) {
              continue; // Already showing, skip
            }
            
            // Check if reminder is completed
            const completedKey = `reminder-completed-${reminderId}`;
            if (localStorage.getItem(completedKey)) {
              continue; // Already completed
            }
            
            // CRITICAL FIX: Mark as shown PERMANENTLY BEFORE triggering popup (so it doesn't show again even if closed)
            markReminderAsShown(reminderId, targetMs);
            
            // Reminder is due - trigger popup
            handleReminderPopup({
              reminderId: reminderId,
              refId: reminderId,
              type: 'reminder',
              title: notif.title || notif.text?.replace('תזכורת: ', '') || 'תזכורת',
              noteText: notif.noteText || '',
              reminderTargetMs: targetMs,
              reminderIsoLocal: notif.reminderIsoLocal,
              reminderTimeZone: notif.reminderTimeZone || userTimeZone,
              deadline: notif.deadline,
              taskId: notif.taskId
            });
          }
        }
      } catch (err) {
        // AUTH_BLOCKED is expected when not logged in or session expired - do not log
        if ((err as any)?.message === 'AUTH_BLOCKED') return;
        // Silently fail - polling is a fallback, not critical
        if (process.env.NODE_ENV === 'development') {
          rateLog('reminder-polling-notifications-error', 'debug', 15000, '[GlobalReminderPopup] Polling notifications failed:', err);
        }
      }
    };
    
    // Poll using polling config (fallback for missed websocket events)
    pollingInterval = setInterval(checkNotificationsForReminders, getPollingInterval('notificationsList'));
    
    // Initial check after 5 seconds
    setTimeout(checkNotificationsForReminders, 5000);
    
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [company, clientData?._id, clientData?.role, handleReminderPopup, userTimeZone]);
  
  // Listen for close event from ClientProfile (when edit modal opens)
  // CRITICAL FIX: Only close if the event is for the current popup's taskId
  useEffect(() => {
    const handleClosePopup = (event: Event) => {
      const customEvent = event as CustomEvent;
      const eventTaskId = customEvent.detail?.taskId || customEvent.detail?.reminderId;
      
      // CRITICAL FIX: Only close if this event is for the currently showing popup
      // This prevents accidental closing when other reminders are edited
      if (reminderPopup && eventTaskId && String(reminderPopup.taskId) === String(eventTaskId)) {
        logRateLimited('close-reminder-popup-event', () => `📌 [GlobalReminderPopup] Received close-reminder-popup event: ${JSON.stringify(customEvent.detail)}`);
        updateReminderPopup(null);
      } else {
      }
    };
    
    window.addEventListener('close-reminder-popup', handleClosePopup as EventListener);

    return () => {
      window.removeEventListener('close-reminder-popup', handleClosePopup as EventListener);
    };
  }, [reminderPopup]);

  // Load and schedule reminders on mount - pause when tab is hidden
  useEffect(() => {
    // Skip reminders for superadmin (they don't have a company database)
    if (!company || !baseUrl || !clientData?._id || company === 'superadmin' || clientData?.role === 'superadmin') return;

    // CRITICAL: Skip if user is not authenticated (no token)
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    if (!token) return;

    const pollState = remindersPollStateRef.current;
    const wasVisible = wasVisibleRef.current;
    const justBecameVisible = wasVisible === false && isVisible === true;
    
    // Update visibility ref
    wasVisibleRef.current = isVisible;

    // Pause polling when tab is hidden
    if (!isVisible) {
      if (process.env.NODE_ENV === 'development' && wasVisible !== false) {
        rateLog('reminder-polling-paused', 'debug', 15000, '[GlobalReminderPopup] Tab is hidden - pausing reminder polling');
      }
      // Clear any existing timer
      if (pollState.timer) {
        clearTimeout(pollState.timer);
        pollState.timer = null;
      }
      return;
    }

    let cancelled = false;

    const scheduleNext = (delay: number) => {
      if (pollState.timer) {
        clearTimeout(pollState.timer);
      }
      if (cancelled || !isVisible) return; // Don't schedule if tab is hidden
      pollState.timer = setTimeout(run, delay);
    };

    const run = async () => {
      if (cancelled || !isVisible) return; // Don't run if tab is hidden
      // CRITICAL: Skip if network gate is active
      if (shouldSkipNetwork()) {
        scheduleNext(getPollingInterval('reminders'));
        return;
      }
      if (pollState.inFlight) {
        scheduleNext(getPollingInterval('reminders'));
        return;
      }

      pollState.inFlight = true;
      try {
        const result = await fetchReminders();
        if (cancelled || !isVisible) return; // Check again after async operation

        if (result.success) {
          markNetworkUp(); // Mark network as up on success
          pollState.failures = 0;
          
          // CRITICAL FIX: Use adaptive polling - check more frequently when reminders are approaching
          // Check if there are reminders due within the next 2 minutes
          const now = Date.now();
          const hasUpcomingReminders = result.upcomingReminders && result.upcomingReminders.length > 0;
          
          if (hasUpcomingReminders) {
            // If reminders are approaching (within 2 minutes), poll every 5 seconds for accuracy
            const nextPollDelay = 5000;
            scheduleNext(nextPollDelay);
          } else {
            // Use normal polling config interval
            scheduleNext(getPollingInterval('reminders'));
          }
        } else {
          // Check if error is network-related
          if (result.error && isNetworkError(result.error)) {
            markNetworkDown(30000, { key: 'reminders-polling', reason: result.error?.message || 'Network error' });
          }
          pollState.failures += 1;
          const backoff = Math.min(10000, 2000 * Math.pow(2, Math.max(0, pollState.failures - 1)));
          // Error handling without showing toast to user
          scheduleNext(backoff);
        }
      } finally {
        pollState.inFlight = false;
      }
    };

    // Always start polling if tab is visible
    if (isVisible) {
      if (justBecameVisible) {
        rateLog('reminder-polling-resumed', 'log', 15000, '🔔 [GlobalReminderPopup] Tab just became visible - resuming reminder polling');
        run();
      } else if (wasVisible === null || wasVisible === undefined) {
        // Initial mount - start polling immediately
        rateOnce('reminder-polling-initial-mount', 'log', '🔔 [GlobalReminderPopup] ⚡ Initial mount - starting reminder polling NOW');
        // Start immediately, don't wait
        setTimeout(() => run(), 0);
      } else if (wasVisible === true) {
        // Tab was already visible, ensure polling continues
        if (!pollState.timer && !pollState.inFlight) {
          rateLog('reminder-polling-restart', 'log', 15000, '🔔 [GlobalReminderPopup] Tab visible but no timer - restarting polling');
          run();
        }
      }
    }

    return () => {
      cancelled = true;
      if (pollState.timer) {
        clearTimeout(pollState.timer);
        pollState.timer = null;
      }
      pollState.failures = 0;
      pollState.inFlight = false;
    };
  }, [company, baseUrl, clientData?._id, fetchReminders, isVisible]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
    };
  }, []);

  // Complete reminder handler - Optimistic update for instant response
  const handleCompleteReminder = async () => {
    if (!reminderPopup || completingReminder) return;
    
    // Save reference for error handling
    const popupData = reminderPopup;
    const reminderId = popupData.taskId; // Extract reminderId from popupData
    const noteText = popupData.description || '';
    const reminderKey = `reminder-${reminderId}-${popupData.targetMs || Date.now()}`; // Construct reminderKey for error handling
    
    // Optimistic update: Close popup immediately and mark as completed in localStorage
    updateReminderPopup(null);
    const completedKey = `reminder-completed-${reminderId}`;
    localStorage.setItem(completedKey, String(Date.now()));
    
    // CRITICAL FIX: Dispatch event IMMEDIATELY to update bell icon (optimistic update)
    // Extract clean note text if it starts with "תזכורת להערה:"
    let cleanNoteText = noteText;
    if (noteText && noteText.startsWith('תזכורת להערה:')) {
      cleanNoteText = noteText.replace('תזכורת להערה:', '').trim();
    }
    
    if (cleanNoteText) {
      // Dispatch event immediately (before API call) to update bell icon instantly
      window.dispatchEvent(new CustomEvent('reminder-completed', {
        detail: { noteText: cleanNoteText, taskId: reminderId, bellActive: false }
      }));
    }
    
    // CRITICAL FIX: Don't show toast here - show it only after API success to prevent duplicates
    // toast.success('תזכורת הושלמה'); // Removed - will show after API success
    
    // Make API call in background
    setCompletingReminder(true);
    
    // Broadcast completion to other tabs
    // CRITICAL FIX: Include tabId so same tab doesn't process its own message
    if (broadcastChannelRef.current) {
      const tabId = sessionStorage.getItem('tabId') || 'default';
      broadcastChannelRef.current.postMessage({
        type: 'reminder:completed',
        reminderId: reminderId,
        targetMs: popupData.targetMs,
        tabId // Include tabId to prevent self-processing
      });
    }
    
    try {
      // Use new completeReminder API (handles both reminder and task status update)
      try {
        await completeReminder(company, reminderId, true); // updateTaskStatus = true
        
        // CRITICAL FIX: Show toast only once after successful API call
        toast.success('תזכורת הושלמה', { toastId: `reminder-completed-${reminderId}` });
        
        // Clear failed status on success
        failedRemindersRef.current.delete(String(reminderId));
        localStorage.removeItem(`reminder-error-cooldown-${reminderId}`);
        
        // Clear any scheduled timeout
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        
        // Dispatch events
        window.dispatchEvent(new CustomEvent('reminder-popup-dismissed', {
          detail: { taskId: reminderId }
        }));
        
        return; // Success
      } catch (completeErr: any) {
        // If reminder not found (404), try to find it by noteText
        if (completeErr.response?.status === 404 && noteText && noteText.startsWith('תזכורת להערה:')) {
          try {
            const cleanNoteText = noteText.replace('תזכורת להערה:', '').trim();
            const clientId = (popupData as any).clientId || clientData?._id;
            if (cleanNoteText && clientId) {
              const { getReminderByNote } = await import('../api/remindersApi');
              const reminderByNote = await getReminderByNote(company, cleanNoteText, clientId);
              if (reminderByNote?.reminder?._id) {
                await completeReminder(company, reminderByNote.reminder._id, true);
                // CRITICAL FIX: Use same toastId to prevent duplicates
                toast.success('תזכורת הושלמה', { toastId: `reminder-completed-${reminderId}` });
                // Clear failed status on success
                failedRemindersRef.current.delete(String(reminderId));
                localStorage.removeItem(`reminder-error-cooldown-${reminderId}`);
                return; // Success
              }
            }
          } catch (noteErr) {
            rateLog('reminder-find-by-note-error', 'warn', 15000, '[GlobalReminderPopup] Failed to find reminder by noteText:', noteErr);
          }
        }
        
        // If completeReminder fails and it's not a 404, show error
        if (completeErr.response?.status !== 404) {
          throw completeErr;
        }
        
        // 404 means reminder doesn't exist - might be a task-only reminder
        // For task-only reminders, we can't update reminder-specific fields via the task update endpoint
        // The updateManagerTask endpoint only accepts: title, description, status, startAt, deadline, kind, clients, isCalendar, remindBeforeMinutes
        // Since bellActive, reminderStatus, and completedAtMs are not in the allowed list, we'll just mark as shown
        rateLog('reminder-not-found-legacy', 'warn', 15000, '[GlobalReminderPopup] Reminder not found - marking as shown (task-only reminder)');
        
        // Mark reminder as completed in localStorage for UI consistency
        const completedKey = `reminder-completed-${reminderId}`;
        localStorage.setItem(completedKey, String(Date.now()));
        
        // Show success message - the reminder won't reappear
        toast.success('תזכורת הושלמה', { toastId: `reminder-completed-${reminderId}` });
        
        // Clear failed status
        failedRemindersRef.current.delete(String(reminderId));
        localStorage.removeItem(`reminder-error-cooldown-${reminderId}`);
        
        // Clear timeout
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current as NodeJS.Timeout);
          timeoutRef.current = null;
        }
        
        // Dispatch events
        window.dispatchEvent(new CustomEvent('reminder-popup-dismissed', {
          detail: { taskId: reminderId }
        }));
        
        return; // Success - reminder marked as shown, won't reappear
      }
    } catch (err: any) {
      errorRateLimited('reminder-complete-error', () => `[GlobalReminderPopup] Error completing reminder: ${err?.message || err}`);
      
      // Determine if error is recoverable (should restore popup) or permanent
      const isPermanentError = err?.response?.status === 404 || err?.response?.status === 400;
      const errorMessage = err?.response?.data?.error || err?.message || 'שגיאה בסימון התזכורת כבוצעה';
      
      // Use unique toastId to prevent duplicate error messages
      const errorToastId = `reminder-complete-error-${reminderId}`;
      
      // Clear the completed flag
      localStorage.removeItem(completedKey);
      
      // Track this failure to prevent popup from reappearing immediately
      failedRemindersRef.current.set(String(reminderId), Date.now());
      // Clean up old entries (older than 1 minute)
      const now = Date.now();
      for (const [id, timestamp] of failedRemindersRef.current.entries()) {
        if (now - timestamp > 60000) {
          failedRemindersRef.current.delete(id);
        }
      }
      
      // CRITICAL FIX: After ANY error when user clicks "Mark as done", mark reminder as shown
      // This prevents the popup from reappearing repeatedly. The user has already attempted to complete it.
      // If they want to try again, they can manually create a new reminder or the system will handle it.
      markReminderAsShown(String(reminderId), popupData.targetMs || Date.now());
      shownRemindersRef.current.add(reminderKey);
      
      // Don't restore popup after error - user has already seen it and attempted to complete it
      // Marking as shown prevents polling from re-triggering it
      
      // Provide more helpful error message based on error type
      let userFriendlyError = errorMessage;
      if (err?.response?.status === 500) {
        userFriendlyError = 'שגיאת שרת - אנא נסה שוב מאוחר יותר או פנה לתמיכה';
      } else if (err?.response?.status === 404) {
        userFriendlyError = 'התזכורת לא נמצאה - יתכן שכבר הושלמה';
      }
      
      toast.error(userFriendlyError, { toastId: errorToastId });
      
      // Log the error for debugging but don't restore popup
      console.warn('🔔 [GlobalReminderPopup] Reminder completion failed, marking as shown to prevent re-appearance:', {
        reminderId,
        error: errorMessage,
        status: err?.response?.status,
        isPermanentError,
        userFriendlyError
      });
    } finally {
      setCompletingReminder(false);
    }
  };

  // Remind me later handler
  const handleRemindLater = async () => {
    if (!reminderPopup || !remindLaterDateTime || reschedulingReminder) return;

    const tzData = localDateTimeToTimezoneAware(remindLaterDateTime);
    if (!tzData) {
      toast.error('יש לבחור תאריך ושעה תקינים');
      return;
    }
    
    const selectedDate = new Date(tzData.targetMs);
    const now = new Date();
    if (selectedDate <= now) {
      toast.error('יש לבחור תאריך ושעה עתידיים');
      return;
    }

    setReschedulingReminder(true);
    try {
      // The picked time is when the user wants to be reminded (trigger time)
      const newTriggerMs = tzData.targetMs;

      // Try to update as a Reminder first (new API)
      try {
        // Fetch current reminder to preserve remindBeforeMinutes
        const reminderRes = await getReminder(company, reminderPopup.taskId);
        const currentReminder = (reminderRes as any)?.reminder;
        
        if (!currentReminder) {
          throw new Error('Reminder not found');
        }

        // Update reminder with new time - keep bellActive=true (bell stays GREEN)
        const updatePayload = {
          reminderIsoLocal: tzData.isoLocal,
          reminderTargetMs: newTriggerMs,
          reminderTimeZone: tzData.timeZone,
          reminderDeliveredAtMs: null, // Reset delivery status
          reminderStatus: 'ACTIVE', // Reset to ACTIVE for new schedule
          bellActive: true // Keep bell GREEN when rescheduling
        };

        const updatedReminderRes = await updateReminder(company, reminderPopup.taskId, updatePayload);
        
        const updatedReminder = (updatedReminderRes as any)?.reminder;
        
        // Cancel old timeout
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }

        // Clear old localStorage lock so new reminder can fire
        const lockKey = `reminder-lock-${reminderPopup.taskId}`;
        localStorage.removeItem(lockKey);

        // Schedule new reminder
        const taskLike: Task = {
          _id: reminderPopup.taskId,
          title: reminderPopup.title || 'תזכורת',
          description: reminderPopup.description || '',
          reminderTargetMs: newTriggerMs,
          reminderIsoLocal: tzData.isoLocal,
          reminderTimeZone: tzData.timeZone,
          deadline: new Date(newTriggerMs).toISOString(),
          isCalendar: true,
          status: '01',
          managerId: currentReminder.createdBy?._id || currentReminder.clientId,
          remindBeforeMinutes: currentReminder.remindBeforeMinutes
        } as Task;
        scheduleReminder(taskLike);

        // CRITICAL FIX: Use toastId to prevent duplicates
        toast.success('תזכורת נדחתה', { toastId: `reminder-rescheduled-${reminderPopup.taskId}` });
        
        // Extract note text and emit rescheduled event for inline note update
        const noteText = reminderPopup.description || '';
        
        // Emit event to ClientProfile to update the label time in UI
        if (noteText) {
          window.dispatchEvent(new CustomEvent('reminder-rescheduled', {
            detail: {
              taskId: reminderPopup.taskId,
              noteText,
              reminderInfo: {
                bellActive: true,
                reminderIsoLocal: tzData.isoLocal,
                reminderTargetMs: newTriggerMs,
                reminderTimeZone: tzData.timeZone
              }
            }
          }));
        }
        
        updateReminderPopup(null);
        setRemindLaterDateTime('');
        return;
      } catch (reminderErr: any) {
        // If it's not a reminder (404), try legacy task rescheduling
        if (reminderErr.response?.status !== 404) {
          throw reminderErr;
        }
      }
      
      // Legacy task rescheduling (for backward compatibility)
      const rolePrefix = isAdmin ? 'admin' : 'manager';
      let task: Task | null = null;
      try {
        if (rolePrefix === 'admin') {
          const taskRes = await getAdminTaskById(company, reminderPopup.taskId);
          task = (taskRes as any)?.task || taskRes;
        } else {
          const taskRes = await getManagerTaskById(company, reminderPopup.taskId);
          task = (taskRes as any)?.task || taskRes;
        }
      } catch {
        const taskRes = await getAdminTaskById(company, reminderPopup.taskId);
        task = (taskRes as any)?.task || taskRes;
      }

      if (!task) {
        toast.error('לא נמצאה המשימה');
        return;
      }

      const legacyTriggerMs = tzData.targetMs;
      const newDeadlineMs = tzData.targetMs;

      const updatePayload: any = {
        deadline: new Date(newDeadlineMs).toISOString(),
        reminderIsoLocal: tzData.isoLocal,
        reminderTargetMs: legacyTriggerMs,
        reminderTimeZone: tzData.timeZone,
        triggerAt: new Date(legacyTriggerMs).toISOString(),
        reminderSentAt: null,
        reminderDeliveredAtMs: null,
        bellActive: true,
        reminderStatus: 'pending'
      };

      // Use API helpers directly instead of building URLs
      let success = false;
      try {
        if (rolePrefix === 'admin') {
          await updateAdminTask(company, reminderPopup.taskId, updatePayload);
        } else {
          await updateManagerTask(company, reminderPopup.taskId, updatePayload);
        }
        success = true;
      } catch {
        try {
          await updateAdminTask(company, reminderPopup.taskId, updatePayload);
          success = true;
        } catch {}
      }

      if (success) {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }

        const lockKey = `reminder-lock-${reminderPopup.taskId}`;
        localStorage.removeItem(lockKey);

        const updatedTask: Task = {
          ...task,
          ...updatePayload,
          reminderTargetMs: legacyTriggerMs,
          reminderIsoLocal: tzData.isoLocal,
          reminderTimeZone: tzData.timeZone
        };
        scheduleReminder(updatedTask);

        // CRITICAL FIX: Use toastId to prevent duplicates
        toast.success('תזכורת נדחתה', { toastId: `reminder-rescheduled-${reminderPopup.taskId}` });
        
        let noteText: string | null = null;
        if (reminderPopup.description && reminderPopup.description.startsWith('תזכורת להערה:')) {
          noteText = reminderPopup.description.replace('תזכורת להערה:', '').trim();
        }
        
        if (noteText) {
          window.dispatchEvent(new CustomEvent('reminder-rescheduled', {
            detail: {
              taskId: reminderPopup.taskId,
              noteText,
              reminderInfo: {
                bellActive: true,
                reminderIsoLocal: tzData.isoLocal,
                reminderTargetMs: legacyTriggerMs,
                reminderTimeZone: tzData.timeZone
              }
            }
          }));
        }
        
        updateReminderPopup(null);
        setRemindLaterDateTime('');
      } else {
        throw new Error('Failed to reschedule reminder');
      }
    } catch (err: any) {
      rateLog('reminder-reschedule-error', 'error', 15000, 'Error rescheduling reminder:', err);
      toast.error(err.response?.data?.error || 'שגיאה בדחיית התזכורת');
    } finally {
      setReschedulingReminder(false);
    }
  };

  // Don't show popup if user is not logged in
  if (!clientData || !clientData._id) {
    // Clear popup if user logs out
    if (reminderPopup) {
      updateReminderPopup(null);
    }
    return null;
  }

  if (!reminderPopup) {
    // No popup to show - this is normal, don't log on every render
    // Only log if we're debugging (can be enabled via localStorage)
    if (typeof window !== 'undefined' && localStorage.getItem('debug-reminder-popup') === 'true') {
    }
    return null;
  }


  // Determine popup position: if pinned and position exists, use saved position; otherwise use default
  // CRITICAL FIX: Validate position is within viewport bounds to prevent off-screen popups
  const getValidatedPosition = (pos: { x: number; y: number } | null) => {
    if (!pos) return null;
    // Ensure position is within viewport
    const maxX = typeof window !== 'undefined' ? window.innerWidth - 320 : 0; // 320px = w-80 width
    const maxY = typeof window !== 'undefined' ? window.innerHeight - 100 : 0;
    return {
      x: Math.max(0, Math.min(pos.x, maxX)),
      y: Math.max(0, Math.min(pos.y, maxY))
    };
  };

  const validatedPosition = position ? getValidatedPosition(position) : null;
  
  const popupStyle: React.CSSProperties = (isPinned && validatedPosition)
    ? { 
        position: 'fixed', 
        left: `${validatedPosition.x}px`, 
        top: `${validatedPosition.y}px`,
        right: 'auto', // Override default right positioning
        cursor: 'default', // Show default cursor when pinned (not draggable)
        zIndex: 9999 // Ensure it's on top
      }
    : (validatedPosition && !isDragging)
    ? {
        // If we have a saved position but not pinned, use it (user moved it but didn't pin)
        position: 'fixed',
        left: `${validatedPosition.x}px`,
        top: `${validatedPosition.y}px`,
        right: 'auto',
        cursor: 'grab',
        zIndex: 9999
      }
    : { 
        position: 'fixed', 
        top: '1rem', 
        right: '1rem',
        left: 'auto',
        cursor: isDragging ? 'grabbing' : 'grab', // Show grab cursor when not pinned
        zIndex: 9999 // Ensure it's on top
      };

  return (
    <>
      <style>{`
        @keyframes slideInFromRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        .reminder-popup-slide {
          animation: slideInFromRight 0.5s ease-out;
          will-change: transform, opacity;
        }
        .reminder-popup-slide * {
          pointer-events: auto !important;
        }
        .reminder-popup-draggable {
          cursor: move;
        }
        .reminder-popup-pinned {
          cursor: default !important;
        }
      `}</style>
      
      <div className="fixed inset-0 z-[9999] pointer-events-none" style={{ zIndex: 9999 }}>
        <div
          ref={popupRef}
          className="w-80 max-w-[90vw] pointer-events-auto reminder-popup-slide"
          dir="rtl"
          style={{
            ...popupStyle,
            zIndex: 10000, // Ensure popup is above the overlay
            visibility: 'visible', // Force visibility
            opacity: 1, // Force opacity
            display: 'block', // Force display
            pointerEvents: 'auto' // CRITICAL: Ensure popup is clickable
          }}
          onClick={(e) => e.stopPropagation()} // Prevent clicks from bubbling to overlay
        >
          <div className="bg-white rounded-lg shadow-xl border border-green-400 overflow-hidden">
            {/* Header - Green section matching image exactly: X and Lock on LEFT, Title and Bell on RIGHT */}
            <div className="bg-green-500 p-3 flex items-center justify-between">
              {/* Left side: Close (X) and Lock buttons */}
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    updateReminderPopup(null);
                  }}
                  className="p-1.5 rounded bg-white/10 hover:bg-white/20 transition flex-shrink-0 cursor-pointer"
                  title="סגור"
                  style={{ pointerEvents: 'auto' }}
                >
                  <FiX className="text-white text-sm" />
                </button>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (reminderPopup) {
                      const noteText = reminderPopup.description || '';
                      const editEvent = new CustomEvent('edit-reminder-from-popup', {
                        detail: {
                          taskId: reminderPopup.taskId,
                          noteText: noteText,
                          reminderId: reminderPopup.taskId,
                          title: reminderPopup.title,
                          targetMs: reminderPopup.targetMs,
                          timeZone: reminderPopup.timeZone,
                          isoLocal: reminderPopup.isoLocal
                        }
                      });
                      window.dispatchEvent(editEvent);
                    }
                    const newPinnedState = !isPinned;
                    if (newPinnedState && popupRef.current) {
                      const rect = popupRef.current.getBoundingClientRect();
                      const pos = { x: rect.left, y: rect.top };
                      setPosition(pos);
                    }
                    setIsPinned(newPinnedState);
                  }}
                  className={`p-1.5 rounded ${isPinned ? 'bg-white/30' : 'bg-white/10'} hover:bg-white/20 transition flex-shrink-0 cursor-pointer`}
                  title={isPinned ? 'בטל נעילה ולערוך תזכורת' : 'נעל מיקום ולערוך תזכורת'}
                  style={{ pointerEvents: 'auto' }}
                >
                  {isPinned ? (
                    <FiLock className="text-white text-sm" />
                  ) : (
                    <FiUnlock className="text-white text-sm" />
                  )}
                </button>
              </div>
              
              {/* Right side: Title and Bell - draggable area */}
              <div
                className={`flex items-center gap-2 flex-1 justify-end ${
                  isPinned ? 'cursor-default' : 'cursor-move'
                }`}
                onMouseDown={handleMouseDown}
                title={isPinned ? 'נעול' : 'גרור כדי להזיז'}
              >
                <div className="text-right">
                  <h3 className="text-base font-bold text-white">תזכורת!</h3>
                  <p className="text-xs text-green-100">הגיע הזמן למשימה שלך</p>
                </div>
                <div className="bg-white/20 rounded-full p-1.5 flex-shrink-0">
                  <FiBell className="text-lg text-white" />
                </div>
                <FiMove className="text-white text-sm flex-shrink-0 opacity-50" />
              </div>
            </div>

            {/* Content */}
            <div className="p-3 text-right space-y-2 max-h-[60vh] overflow-y-auto">
              <div>
                <div className="text-xs font-semibold text-gray-500 mb-0.5">כותרת:</div>
                <div className="text-sm text-gray-900 font-bold break-words">{reminderPopup.title}</div>
              </div>

              {reminderPopup.description && (
                <div>
                  <div className="text-xs font-semibold text-gray-500 mb-0.5">תיאור:</div>
                  <div className="text-gray-700 whitespace-pre-line text-xs break-words">{reminderPopup.description}</div>
                </div>
              )}

              {(reminderPopup.deadline || reminderPopup.targetMs) && (
                <div>
                  <div className="text-xs font-semibold text-gray-500 mb-0.5">מועד:</div>
                  <div className="text-gray-700 text-xs">
                    {(() => {
                      // Use targetMs if available (more accurate - this is what user picked), otherwise use deadline
                      let date: Date;
                      if (reminderPopup.targetMs) {
                        date = new Date(reminderPopup.targetMs);
                      } else if (reminderPopup.deadline) {
                        date = new Date(reminderPopup.deadline);
                      } else {
                        return '';
                      }
                      
                      const timeZone = reminderPopup.timeZone || 'Asia/Jerusalem';
                      
                      // Format to match comment format: DD.MM.YYYY, HH:MM (no space before comma)
                      // Use formatToParts for precise control
                      const dateParts = new Intl.DateTimeFormat('en-GB', {
                        timeZone,
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                      }).formatToParts(date);
                      
                      const timeParts = new Intl.DateTimeFormat('en-GB', {
                        timeZone,
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false,
                      }).formatToParts(date);
                      
                      const day = dateParts.find(p => p.type === 'day')?.value || '';
                      const month = dateParts.find(p => p.type === 'month')?.value || '';
                      const year = dateParts.find(p => p.type === 'year')?.value || '';
                      const hour = timeParts.find(p => p.type === 'hour')?.value || '';
                      const minute = timeParts.find(p => p.type === 'minute')?.value || '';
                      
                      // Format as HH:MM, DD.MM.YYYY (time first, then date with comma) - matching image
                      return `${hour}:${minute}, ${day}.${month}.${year}`;
                    })()}
                  </div>
                </div>
              )}

              {/* Remind me later input */}
              <div>
                <div className="text-xs font-semibold text-gray-500 mb-0.5">הזכר לי מאוחר יותר:</div>
                <div className="relative">
                  <input
                    type="datetime-local"
                    value={remindLaterDateTime}
                    onChange={(e) => setRemindLaterDateTime(e.target.value)}
                    className="w-full px-2 py-1.5 pr-8 border border-gray-300 rounded text-xs"
                    placeholder="dd/mm/yyyy, --:--"
                    disabled={reschedulingReminder}
                    style={{ pointerEvents: 'auto' }}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <FiClock className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm pointer-events-none" />
                  {remindLaterDateTime && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleRemindLater();
                      }}
                      disabled={reschedulingReminder || completingReminder}
                      className="mt-1 w-full px-2 py-1 text-xs rounded bg-blue-600 hover:bg-blue-700 text-white transition disabled:opacity-50 cursor-pointer"
                      style={{ pointerEvents: 'auto' }}
                    >
                      שמור
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Action buttons - Green footer matching image exactly: סגור (gray) on LEFT, סמן כבוצע (green) on RIGHT */}
            <div className="p-3 bg-green-500 flex justify-between items-center gap-2">
              {/* Left: Close button (gray) */}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  updateReminderPopup(null);
                }}
                disabled={completingReminder || reschedulingReminder}
                className="px-4 py-2 text-sm rounded bg-gray-200 hover:bg-gray-300 text-gray-700 transition disabled:opacity-50 cursor-pointer"
                style={{ pointerEvents: 'auto' }}
              >
                סגור
              </button>
              
              {/* Right: Mark as complete button (green) */}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleCompleteReminder();
                }}
                disabled={completingReminder || reschedulingReminder}
                className="px-4 py-2 text-sm rounded bg-green-600 hover:bg-green-700 text-white transition disabled:opacity-50 flex items-center gap-1.5 cursor-pointer"
                style={{ pointerEvents: 'auto' }}
              >
                <FiCheckCircle className="text-base" />
                <span>סמן כבוצע</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
